import api from '../../services/api';

/**
 * Hằng + tiện ích dùng chung cho phân hệ Báo cơm.
 * Nhãn/tông màu khớp với `labels[].tone` mà backend trả (services/meal.service.rowLabels).
 */

export const TONE_VARIANT = { neutral: 'neutral', success: 'success', warning: 'warning', danger: 'danger', info: 'info', brand: 'brand' };

export const STATUS_LABEL = { eating: 'Ăn', not_eating: 'Không ăn', default: 'Theo mặc định', walkin: 'Phát sinh' };

export const PRINT_MODE_LABEL = {
  browser: 'In qua trình duyệt (máy tính ở quầy nối máy in)',
  agent: 'Agent in đặt tại căn tin (kéo hàng đợi từ máy chủ)',
  network: 'Máy chủ gửi thẳng tới máy in mạng (IP:9100)',
};

export const PRINT_STATUS_LABEL = { none: 'Chưa in', queued: 'Đang chờ in', printed: 'Đã in', failed: 'In lỗi' };

const pad = (n) => String(n).padStart(2, '0');
export const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
export const monthLocal = () => todayLocal().slice(0, 7);
export const addDaysLocal = (dateStr, n) => { const [y, m, d] = dateStr.split('-').map(Number); const x = new Date(y, m - 1, d + n); return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`; };
export const fmtDate = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');
export const fmtDateShort = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '');
export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : '');
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '');
export const VN_DOW = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
export const dowOf = (s) => { const [y, m, d] = s.split('-').map(Number); return VN_DOW[new Date(y, m - 1, d).getDay()]; };
export const fmtMoney = (n) => (n == null ? '' : Number(n).toLocaleString('vi-VN'));

/** Thông điệp lỗi từ axios → chuỗi. */
export const errMsg = (e, fallback = 'Có lỗi xảy ra') => e?.response?.data?.message || e?.message || fallback;

/**
 * In một trang HTML (phiếu) bằng iframe ẩn — không mở cửa sổ mới (bị chặn popup trên
 * điện thoại), không rời khỏi màn hình quầy. Chrome ở quầy chạy với cờ --kiosk-printing
 * thì in thẳng ra máy mặc định, không hiện hộp thoại.
 */
export function printHtml(html) {
  return new Promise((resolve) => {
    const old = document.getElementById('meal-print-frame');
    if (old) old.remove();
    const f = document.createElement('iframe');
    f.id = 'meal-print-frame';
    f.setAttribute('aria-hidden', 'true');
    f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;';
    document.body.appendChild(f);
    let done = false;
    const finish = () => { if (done) return; done = true; setTimeout(() => f.remove(), 30000); resolve(); };
    f.onload = () => {
      try {
        const w = f.contentWindow;
        w.focus();
        w.onafterprint = finish;
        setTimeout(() => { try { w.print(); } catch (e) { /* bỏ qua */ } setTimeout(finish, 1500); }, 150);
      } catch (e) { finish(); }
    };
    f.srcdoc = html;
  });
}

/** Tải HTML phiếu rồi in; báo đã in cho máy chủ. */
export async function printTicket(ticketId) {
  const r = await api.get(`/meals/tickets/${ticketId}/print`, { responseType: 'text', transformResponse: [(d) => d] });
  await printHtml(r.data);
  try { await api.post(`/meals/tickets/${ticketId}/printed`); } catch (e) { /* đếm số lần in không quan trọng bằng tờ phiếu */ }
}

/** Lấy SVG QR của phiếu (có Authorization). */
export async function fetchTicketQr(ticketId) {
  const r = await api.get(`/meals/tickets/${ticketId}/qr.svg`, { responseType: 'text', transformResponse: [(d) => d] });
  return r.data;
}

export function cutoffText(cutoff) {
  if (!cutoff) return '';
  if (cutoff.is_past) return `Đã qua giờ chốt ${cutoff.cutoff_label}`;
  const m = cutoff.minutes_left;
  if (m >= 60 * 24) return `Chốt lúc ${cutoff.cutoff_label}`;
  if (m >= 60) return `Chốt lúc ${cutoff.cutoff_label} · còn ${Math.floor(m / 60)}g${m % 60 ? ` ${m % 60}p` : ''}`;
  return `Chốt lúc ${cutoff.cutoff_label} · còn ${m} phút`;
}

const DOW_FULL = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];
/** "Thứ tư 14/10" */
export const fmtDowDate = (s) => {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  return `${DOW_FULL[new Date(y, m - 1, d).getDay()]} ${pad(d)}/${pad(m)}`;
};
/** "còn 25 phút" / "còn 3 giờ 10 phút" / "đã chốt" */
export function leftText(cutoff) {
  if (!cutoff) return '';
  if (cutoff.is_past) return 'đã chốt';
  const m = Number(cutoff.minutes_left) || 0;
  if (m >= 60 * 24) return `chốt ${cutoff.cutoff_label}`;
  if (m >= 60) return `còn ${Math.floor(m / 60)} giờ${m % 60 ? ` ${m % 60} phút` : ''}`;
  return `còn ${m} phút`;
}
/** Mở cuộc gọi: bỏ khoảng trắng trong số. */
export const telHref = (phone) => `tel:${String(phone || '').replace(/\s+/g, '')}`;

/** Tải bảng báo cơm (HTML) rồi in bằng iframe ẩn. params: location_id, date, slot_id, format(a4|k80), names, department_id, kind. */
export async function printOrderSheet(params) {
  const r = await api.get('/meals/orders/print', { params, responseType: 'text', transformResponse: [(d) => d] });
  await printHtml(r.data);
}

/** Bữa nào còn thiếu việc tiếp theo: ưu tiên bữa chưa chốt và chưa báo. */
export function nextOpenSlot(strip, currentId) {
  return (strip || []).find((s) => s.slot_id !== currentId && !s.cutoff?.is_past && !s.reported && !s.is_closed) || null;
}
